import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadApiBlob } from "../../../lib/api";
import type {
  PolicyAnalysis,
  PolicyValue,
} from "../../../types/insuranceRecovery";

const fields: Record<string, string> = {
  insurance_company: "Carrier",
  policy_number: "Policy number",
  deductible: "Deductible",
  dwelling_coverage: "Dwelling coverage",
  personal_property_coverage: "Personal property coverage",
  liability_coverage: "Liability coverage",
};
const errorText = (error: unknown) => {
  const body = (error as { responseData?: unknown })?.responseData;
  return body
    ? typeof body === "string"
      ? body
      : JSON.stringify(body)
    : "The request failed. Please retry.";
};

export default function PolicyResults({
  analysis: initial,
  jobId,
}: {
  analysis: PolicyAnalysis;
  jobId?: number | null;
}) {
  const cache = useQueryClient();
  const key = ["policy-source-review", initial.id];
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      (
        await api.get<PolicyAnalysis>(
          `/api/policy-copilot/analyses/${initial.id}/`,
        )
      ).data,
    initialData: initial,
    refetchInterval: (q) =>
      ["pending", "processing"].includes(q.state.data?.status || "")
        ? 2000
        : false,
  });
  const analysis = query.data;
  const [editing, setEditing] = useState(false);
  const [revision, setRevision] = useState(analysis.revision);
  const [values, setValues] = useState<Record<string, PolicyValue>>({});
  const [note, setNote] = useState("");
  const [manualPage, setManualPage] = useState<number | null>(null);
  const [manualText, setManualText] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [target, setTarget] = useState<number | null>(jobId || null);
  const [message, setMessage] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const pending = ["pending", "processing"].includes(analysis.status);
  const pages = analysis.document?.pages || [];
  const reviewed = Object.keys(analysis.reviewed_values || {}).length > 0;
  const mutation = useMutation({
    mutationFn: async ({ action, data }: { action: string; data: unknown }) =>
      (
        await api.post<PolicyAnalysis>(
          `/api/policy-copilot/analyses/${analysis.id}/${action}/`,
          data,
        )
      ).data,
    onSuccess: (data) => {
      cache.setQueryData(key, data);
      setEditing(false);
      setManualPage(null);
      setMessage("Review saved.");
    },
  });
  const save = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ updated: Record<string, string>; skipped: string[] }>(
          `/api/policy-copilot/analyses/${analysis.id}/save-to-job/`,
          { job: target },
        )
      ).data,
    onSuccess: (data) =>
      setMessage(
        `Saved ${Object.keys(data.updated).length} reviewed values. ${data.skipped.length} existing job values kept.`,
      ),
  });
  const jobs = useQuery({
    queryKey: ["policy-review-jobs", analysis.contact],
    enabled: !!analysis.contact && !jobId && reviewed,
    queryFn: async () => {
      const data = (
        await api.get<
          | { id: number; job_number: string }[]
          | { results: { id: number; job_number: string }[] }
        >(`/api/jobs/?contacts=${analysis.contact}`)
      ).data;
      return Array.isArray(data) ? data : data.results;
    },
  });
  const startReview = () => {
    setRevision(analysis.revision);
    setNote("");
    setValues(
      Object.fromEntries(
        Object.keys(fields).map((name) => [
          name,
          analysis.reviewed_values?.[name] || {
            value:
              typeof analysis.policy_data?.[name] === "string"
                ? (analysis.policy_data[name] as string)
                : "",
            page: 1,
            quote: "",
            correction: "",
          },
        ]),
      ),
    );
    setEditing(true);
    mutation.reset();
  };
  const update = (
    name: string,
    field: keyof PolicyValue,
    value: string | number,
  ) => setValues({ ...values, [name]: { ...values[name], [field]: value } });
  return (
    <Stack spacing={2} data-testid="policy-source-review">
      <Card>
        <Typography level="h3" sx={{ overflowWrap: "anywhere" }}>
          {analysis.file_name}
        </Typography>
        <Box>
          <Chip color={reviewed ? "success" : "warning"}>
            {reviewed
              ? "Values reviewed"
              : pending
                ? "Extracting source pages"
                : "Needs source review"}
          </Chip>
        </Box>
        <Typography level="body-sm">
          Uploaded by {analysis.analyzed_by_name} · {analysis.page_count} pages
          · Review {analysis.revision}
        </Typography>
        {pending && (
          <Alert>
            Local extraction is running. You can leave this page and return
            through Recent Analyses.
          </Alert>
        )}
        {analysis.error_message && (
          <Alert color="warning">{analysis.error_message}</Alert>
        )}
        {["failed", "pending", "processing"].includes(analysis.status) && (
          <Button
            variant="outlined"
            onClick={() => mutation.mutate({ action: "retry", data: {} })}
            loading={mutation.isPending}
          >
            Retry failed or stalled extraction
          </Button>
        )}
        {analysis.sources?.map((source) => (
          <Button
            key={source.position}
            variant="outlined"
            onClick={async () => {
              try {
                await downloadApiBlob(
                  `/api/policy-copilot/analyses/${analysis.id}/sources/${source.position}/`,
                  source.name,
                );
              } catch (error) {
                setDownloadError(errorText(error));
              }
            }}
          >
            Download original: {source.name}
          </Button>
        ))}
      </Card>
      {(mutation.error || save.error || downloadError) && (
        <Alert color="danger">
          {downloadError || errorText(mutation.error || save.error)}
        </Alert>
      )}
      {message && <Alert color="success">{message}</Alert>}
      {analysis.summary && (
        <Card>
          <Typography level="title-lg">AI draft summary</Typography>
          <Typography level="body-sm">
            Verify these suggestions against the original pages. Only explicitly
            reviewed fields can be saved to a job.
          </Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {analysis.summary}
          </Typography>
        </Card>
      )}
      {!pending && (
        <Card>
          <Typography level="h4">Source pages</Typography>
          {(pages.length
            ? pages
            : [
                {
                  page: 1,
                  source: 1,
                  source_page: 1,
                  text: analysis.document?.extracted_text || "",
                  method: "unresolved",
                  readable: false,
                },
              ]
          ).map((page) => (
            <Box
              key={page.page}
              sx={{ borderTop: "1px solid", borderColor: "divider", py: 1 }}
            >
              <Typography level="title-md">
                Page {page.page} · {page.method} ·{" "}
                {page.readable ? "Text available" : "Needs transcription"}
              </Typography>
              <Box component="details">
                <Box component="summary" sx={{ cursor: "pointer" }}>
                  View extracted text
                </Box>
                <Typography
                  level="body-sm"
                  sx={{
                    whiteSpace: "pre-wrap",
                    maxHeight: 300,
                    overflow: "auto",
                    overflowWrap: "anywhere",
                  }}
                >
                  {page.text || "No usable text extracted."}
                </Typography>
              </Box>
              <Button
                variant="plain"
                onClick={() => {
                  setRevision(analysis.revision);
                  setManualPage(page.page);
                  setManualText(page.text);
                  setManualNote("");
                  mutation.reset();
                }}
              >
                Correct page {page.page} text
              </Button>
            </Box>
          ))}
          {manualPage && (
            <Stack spacing={1} data-testid="policy-manual-text">
              <Typography level="title-md">
                Manual transcription · page {manualPage}
              </Typography>
              <Textarea
                aria-label="Page text"
                minRows={6}
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
              />
              <Input
                aria-label="Transcription reason"
                placeholder="Explain the correction"
                value={manualNote}
                onChange={(e) => setManualNote(e.target.value)}
              />
              <Typography level="body-sm">
                Changing source text clears prior value approvals and records
                the before/after text.
              </Typography>
              <Button
                loading={mutation.isPending}
                onClick={() =>
                  mutation.mutate({
                    action: "manual-text",
                    data: {
                      revision,
                      page: manualPage,
                      text: manualText,
                      note: manualNote,
                    },
                  })
                }
              >
                Save corrected text
              </Button>
              <Button variant="plain" onClick={() => setManualPage(null)}>
                Cancel correction
              </Button>
            </Stack>
          )}
        </Card>
      )}
      {!pending && !manualPage && (
        <Card>
          <Typography level="h4">Reviewed claim values</Typography>
          {!editing ? (
            <>
              {reviewed ? (
                Object.entries(analysis.reviewed_values)
                  .filter(([, v]) => v.value)
                  .map(([name, value]) => (
                    <Typography key={name}>
                      {fields[name] || name}: {value.value} · page {value.page}
                    </Typography>
                  ))
              ) : (
                <Typography>
                  No values approved yet. Read the source, correct suggestions,
                  and cite a page or explain manual entries.
                </Typography>
              )}
              <Button onClick={startReview}>Review policy values</Button>
            </>
          ) : (
            <Stack spacing={2} data-testid="policy-values-form">
              {Object.entries(fields).map(([name, label]) => (
                <Box key={name}>
                  <FormLabel>{label}</FormLabel>
                  <Input
                    aria-label={label}
                    value={values[name]?.value || ""}
                    onChange={(e) => update(name, "value", e.target.value)}
                  />
                  {values[name]?.value && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Input
                        aria-label={`${label} source page`}
                        type="number"
                        slotProps={{
                          input: { min: 1, max: analysis.page_count },
                        }}
                        value={values[name].page}
                        onChange={(e) =>
                          update(name, "page", Number(e.target.value))
                        }
                      />
                      <Textarea
                        aria-label={`${label} source quote`}
                        placeholder="Quote the matching source text"
                        value={values[name].quote}
                        onChange={(e) => update(name, "quote", e.target.value)}
                      />
                      <Input
                        aria-label={`${label} correction reason`}
                        placeholder="Explain a manual or normalized value"
                        value={values[name].correction}
                        onChange={(e) =>
                          update(name, "correction", e.target.value)
                        }
                      />
                    </Stack>
                  )}
                </Box>
              ))}
              <Textarea
                aria-label="Policy review reason"
                placeholder="Review note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                loading={mutation.isPending}
                onClick={() =>
                  mutation.mutate({
                    action: "review",
                    data: {
                      revision,
                      note,
                      values: Object.fromEntries(
                        Object.entries(values).filter(([, value]) =>
                          value.value.trim(),
                        ),
                      ),
                    },
                  })
                }
              >
                Approve reviewed values
              </Button>
              <Button variant="plain" onClick={() => setEditing(false)}>
                Cancel review
              </Button>
            </Stack>
          )}
        </Card>
      )}
      {reviewed && (
        <Card>
          <Typography level="h4">Save reviewed values to a job</Typography>
          <Typography>
            Existing job values are preserved. A percentage deductible requires
            a reviewed dollar amount before it can populate the job.
          </Typography>
          {!jobId && (
            <Select
              aria-label="Target job"
              value={target}
              onChange={(_, value) => setTarget(value)}
              placeholder="Choose customer job"
            >
              {jobs.data?.map((job) => (
                <Option key={job.id} value={job.id}>
                  {job.job_number}
                </Option>
              ))}
            </Select>
          )}
          <Button
            disabled={!target}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save reviewed values to job
          </Button>
        </Card>
      )}
      {!!analysis.reviews?.length && (
        <Card>
          <Typography level="h4">Review history</Typography>
          {analysis.reviews.map((review) => (
            <Box key={review.revision}>
              <Typography level="title-sm">
                Revision {review.revision} · {review.actor} ·{" "}
                {new Date(review.at).toLocaleString()}
              </Typography>
              <Typography level="body-sm">
                {review.data.action}: {review.data.note}
              </Typography>
            </Box>
          ))}
        </Card>
      )}
    </Stack>
  );
}
